import { prefillSearchInputFromUrl } from './catalog-search-url.js';

const HISTORY_KEY = 'pc_search_history';
const HISTORY_LIMIT = 8;
const MQ_MOBILE = '(max-width: 991px)';

function isMobile() {
  return window.matchMedia(MQ_MOBILE).matches;
}

function readHistory() {
  try {
    const raw = localStorage.getItem(HISTORY_KEY);
    const list = raw ? JSON.parse(raw) : [];
    return Array.isArray(list) ? list.filter((item) => typeof item === 'string' && item.trim()) : [];
  } catch (error) {
    return [];
  }
}

function writeHistory(items) {
  try {
    localStorage.setItem(HISTORY_KEY, JSON.stringify(items.slice(0, HISTORY_LIMIT)));
  } catch (error) {
    // ignore quota / private mode
  }
}

function rememberQuery(query) {
  const value = String(query || '').trim();
  if (value.length < 2) {
    return;
  }

  const next = [value, ...readHistory().filter((item) => item.toLowerCase() !== value.toLowerCase())];
  writeHistory(next);
}

function closestFromEvent(event, selector) {
  const path = typeof event.composedPath === 'function' ? event.composedPath() : [];
  for (const node of path) {
    if (node instanceof Element && node.matches(selector)) {
      return node;
    }
  }
  return event.target instanceof Element ? event.target.closest(selector) : null;
}

export function initMobileMenu() {
  const sheet = document.getElementById('mobile-menu-sheet');
  const toggles = Array.from(document.querySelectorAll('[data-mobile-menu-toggle]'));
  if (!sheet || !toggles.length || sheet.dataset.mobileMenuInited === '1') {
    return;
  }

  sheet.dataset.mobileMenuInited = '1';

  const setTogglesExpanded = (expanded) => {
    document.querySelectorAll('[data-mobile-menu-toggle]').forEach((toggle) => {
      toggle.setAttribute('aria-expanded', expanded ? 'true' : 'false');
    });
  };

  if (sheet.parentElement !== document.body) {
    document.body.appendChild(sheet);
  }

  const searchHostHeader = document.querySelector('[data-mobile-search-host="header"]');
  const searchHostSheet = document.querySelector('[data-mobile-search-host="sheet"]');
  const historyRoot = sheet.querySelector('[data-mobile-menu-history]');
  const historyList = sheet.querySelector('[data-mobile-menu-history-list]');
  const resultsRoot = document.getElementById('mobile-menu-search-results');
  const clearBtn = () => document.querySelector('#mobile-header-search [data-search-clear]');
  const input = () => document.getElementById('mobile-header-search-input');
  const wrap = () => document.getElementById('mobile-header-search');

  const titleEl = sheet.querySelector('.mobile-menu-sheet__title');

  const sheetOptions = {
    bodyClass: 'catalog-sheet-open',
    extraOpenClasses: ['is-mobile-open'],
    setHidden: true,
  };

  let allowSearchView = false;
  let suppressSearchFocus = false;
  let settledQuery = '';

  const setView = (view) => {
    sheet.dataset.mobileMenuView = view;
    if (titleEl) {
      titleEl.textContent = allowSearchView ? 'Поиск' : 'Меню';
    }
  };

  const syncClear = () => {
    const field = input();
    const button = clearBtn();
    if (!field || !button) {
      return;
    }
    const filled = field.value.trim().length > 0;
    button.hidden = !filled;
    field.closest('.header-search')?.classList.toggle('is-filled', filled);
  };

  const renderHistory = () => {
    if (!historyList) {
      return false;
    }
    const items = readHistory();
    historyList.replaceChildren(
      ...items.map((item) => {
        const li = document.createElement('li');
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'mobile-menu__history-item';
        button.dataset.mobileHistoryQuery = item;
        button.innerHTML = '<svg aria-hidden="true" width="16" height="16"><use href="#icon-clock"></use></svg>';
        const span = document.createElement('span');
        span.textContent = item;
        button.appendChild(span);
        li.appendChild(button);
        return li;
      })
    );
    if (historyRoot) {
      historyRoot.hidden = items.length === 0;
    }
    return items.length > 0;
  };

  const placeSearch = (inSheet) => {
    const searchWrap = wrap();
    if (!searchWrap || !searchHostHeader || !searchHostSheet) {
      return;
    }
    const host = inSheet ? searchHostSheet : searchHostHeader;
    if (searchWrap.parentElement !== host) {
      suppressSearchFocus = true;
      host.appendChild(searchWrap);
      requestAnimationFrame(() => {
        suppressSearchFocus = false;
      });
    }
  };

  const updateViewFromQuery = () => {
    const field = input();
    const query = field ? field.value.trim() : '';
    const hasResults = Boolean(
      resultsRoot && resultsRoot.innerHTML.trim() !== '' && resultsRoot.style.display !== 'none'
    );

    if (query.length >= 2 && hasResults) {
      setView('results');
      return;
    }
    if (allowSearchView && query.length >= 2) {
      if (query === settledQuery && !hasResults) {
        setView('empty');
      }
      return;
    }
    if (renderHistory()) {
      setView('history');
      return;
    }
    setView('nav');
  };

  const openSheet = (fromSearch) => {
    if (!window.CatalogSheet) {
      return;
    }
    if (fromSearch && window.CatalogSheet.isOpen(sheet)) {
      return;
    }
    allowSearchView = Boolean(fromSearch);
    placeSearch(true);
    renderHistory();
    if (fromSearch) {
      updateViewFromQuery();
    } else {
      setView('nav');
    }
    setTogglesExpanded(true);
    window.CatalogSheet.open(sheet, sheetOptions);
    if (fromSearch) {
      requestAnimationFrame(() => {
        input()?.focus();
      });
    }
  };

  const menuNav = sheet.querySelector('[data-mobile-menu-nav]');
  const prefersReducedMotion = () =>
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const setExpandState = (expandedTarget) => {
    sheet.querySelectorAll('[data-mobile-menu-expand]').forEach((button) => {
      const isCurrent = button.getAttribute('data-menu-target') === expandedTarget;
      button.setAttribute('aria-expanded', String(isCurrent));
      button.setAttribute('aria-label', isCurrent ? 'Скрыть подразделы' : 'Показать подразделы');
    });
  };

  let menuAnimToken = 0;
  const MENU_SLIDE_MS = 200;

  const waitTransform = (el, token) =>
    new Promise((resolve) => {
      let done = false;
      const finish = () => {
        if (done) {
          return;
        }
        done = true;
        el.removeEventListener('transitionend', onEnd);
        resolve(token === menuAnimToken);
      };
      const onEnd = (event) => {
        if (event.target === el && event.propertyName === 'transform') {
          finish();
        }
      };
      el.addEventListener('transitionend', onEnd);
      window.setTimeout(finish, MENU_SLIDE_MS + 40);
    });

  const showMenuView = (viewId, { fromRight = true } = {}) => {
    if (!menuNav) {
      return;
    }
    const current = menuNav.querySelector('.mobile-menu__view.is-active');
    const next = menuNav.querySelector(`[data-menu-view="${viewId}"]`);
    if (!next || next === current) {
      return;
    }

    const token = ++menuAnimToken;
    const reduceMotion = prefersReducedMotion();

    const settle = () => {
      if (token !== menuAnimToken) {
        return;
      }
      current?.classList.remove('is-active', 'is-to-left', 'is-to-right', 'is-from-left', 'is-from-right');
      next.classList.remove('is-to-left', 'is-to-right', 'is-from-left', 'is-from-right');
      next.classList.add('is-active');
      next.hidden = false;
      if (current && current !== next) {
        current.classList.remove('is-active');
        current.hidden = true;
      }
      menuNav.classList.remove('is-animating');
      menuNav.style.removeProperty('height');
      menuNav.classList.toggle('is-drill', viewId !== 'root');
      setExpandState(viewId === 'root' ? '' : viewId);
    };

    if (reduceMotion || !current) {
      settle();
      return;
    }

    const run = async () => {
      menuNav.classList.add('is-animating');
      menuNav.style.height = `${current.offsetHeight}px`;
      current.classList.add(fromRight ? 'is-to-left' : 'is-to-right');
      const leaveOk = await waitTransform(current, token);
      if (!leaveOk) {
        return;
      }

      current.classList.remove('is-active', 'is-to-left', 'is-to-right');
      current.hidden = true;

      next.hidden = false;
      next.classList.add(fromRight ? 'is-from-right' : 'is-from-left');
      void next.offsetWidth;
      next.classList.add('is-active');
      next.classList.remove(fromRight ? 'is-from-right' : 'is-from-left');
      menuNav.style.height = `${next.scrollHeight}px`;
      const enterOk = await waitTransform(next, token);
      if (!enterOk) {
        return;
      }
      settle();
    };

    run();
  };

  const collapseMenuItems = () => {
    menuAnimToken += 1;
    if (!menuNav) {
      return;
    }
    menuNav.querySelectorAll('.mobile-menu__view').forEach((view) => {
      view.classList.remove('is-to-left', 'is-to-right', 'is-from-left', 'is-from-right');
      if (view.dataset.menuView === 'root') {
        view.hidden = false;
        view.classList.add('is-active');
      } else {
        view.hidden = true;
        view.classList.remove('is-active');
      }
    });
    menuNav.classList.remove('is-animating', 'is-drill');
    menuNav.style.removeProperty('height');
    setExpandState('');
  };

  const closeSheet = () => {
    if (!window.CatalogSheet) {
      return;
    }
    allowSearchView = false;
    settledQuery = '';
    setTogglesExpanded(false);
    window.CatalogSheet.close(sheet, sheetOptions);
    placeSearch(false);
    setView('nav');
    collapseMenuItems();
    syncClear();
  };

  document.addEventListener('click', (event) => {
    const toggle = closestFromEvent(event, '[data-mobile-menu-toggle]');
    if (toggle) {
      event.preventDefault();
      event.stopPropagation();
      if (!isMobile()) {
        return;
      }
      if (window.CatalogSheet?.isOpen(sheet)) {
        closeSheet();
      } else {
        openSheet(false);
      }
      return;
    }

    if (!isMobile() || suppressSearchFocus) {
      return;
    }
    const searchWrapEl = wrap();
    const onHeaderSearch = closestFromEvent(event, '[data-mobile-search-host="header"]');
    if (!onHeaderSearch || !searchWrapEl || !onHeaderSearch.contains(searchWrapEl)) {
      return;
    }
    if (closestFromEvent(event, '[data-search-clear]')) {
      return;
    }
    openSheet(true);
  });

  sheet.querySelectorAll('[data-catalog-sheet-close]').forEach((el) => {
    el.addEventListener('click', closeSheet);
  });

  sheet.addEventListener('click', (event) => {
    const back = closestFromEvent(event, '[data-mobile-menu-back]');
    if (back && sheet.contains(back)) {
      event.preventDefault();
      event.stopPropagation();
      const parentView = back.getAttribute('data-menu-parent') || 'root';
      showMenuView(parentView, { fromRight: false });
      return;
    }

    const toggle = closestFromEvent(event, '[data-mobile-menu-expand]');
    if (!toggle || !sheet.contains(toggle)) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    const target = toggle.getAttribute('data-menu-target');
    if (target) {
      showMenuView(target, { fromRight: true });
    }
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && window.CatalogSheet?.isOpen(sheet)) {
      closeSheet();
    }
  });

  document.addEventListener('focusin', (event) => {
    if (!isMobile() || suppressSearchFocus) {
      return;
    }
    if (event.target !== input()) {
      return;
    }
    openSheet(true);
  });

  document.addEventListener('input', (event) => {
    if (event.target !== input()) {
      return;
    }
    syncClear();
    if (!window.CatalogSheet?.isOpen(sheet)) {
      return;
    }
    allowSearchView = true;
    if (!event.target.value.trim()) {
      settledQuery = '';
      if (resultsRoot) {
        resultsRoot.innerHTML = '';
        resultsRoot.style.display = 'none';
      }
    }
    updateViewFromQuery();
  });

  document.addEventListener('click', (event) => {
    const target = event.target;
    if (!(target instanceof Element)) {
      return;
    }

    const clear = target.closest('[data-search-clear]');
    if (clear && wrap()?.contains(clear)) {
      event.preventDefault();
      const field = input();
      if (field) {
        field.value = '';
        field.dispatchEvent(new Event('bxchange', { bubbles: true }));
        field.dispatchEvent(new Event('input', { bubbles: true }));
        field.focus();
      }
      settledQuery = '';
      if (resultsRoot) {
        resultsRoot.innerHTML = '';
        resultsRoot.style.display = 'none';
      }
      if (window.CatalogSheet?.isOpen(sheet)) {
        if (renderHistory()) {
          setView('history');
        } else {
          setView('nav');
        }
      }
      syncClear();
      return;
    }

    const historyBtn = target.closest('[data-mobile-history-query]');
    if (historyBtn) {
      const query = historyBtn.getAttribute('data-mobile-history-query') || '';
      const field = input();
      if (field) {
        field.value = query;
        field.dispatchEvent(new Event('bxchange', { bubbles: true }));
        field.dispatchEvent(new Event('input', { bubbles: true }));
        field.focus();
      }
    }
  });

  document.addEventListener('submit', (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !wrap()?.contains(form)) {
      return;
    }
    rememberQuery(input()?.value);
  });

  if (resultsRoot) {
    resultsRoot.addEventListener('click', (event) => {
      const link = event.target.closest('a');
      if (link) {
        rememberQuery(input()?.value);
      }
    });
  }

  const bindSearchControl = () => {
    const control = window.jsControlMobileHeader;
    if (!control || control.__mobileMenuBound) {
      return;
    }
    control.__mobileMenuBound = true;
    const originalShow = control.ShowResult.bind(control);
    control.ShowResult = function patchedShow(result) {
      originalShow(result);
      if (isMobile() && window.CatalogSheet?.isOpen(sheet) && allowSearchView) {
        const current = input()?.value.trim() || '';
        const searched = typeof this.oldValue === 'string' ? this.oldValue.trim() : current;
        if (current !== searched) {
          return;
        }
        settledQuery = current;
        updateViewFromQuery();
      }
    };
  };

  bindSearchControl();
  document.addEventListener('DOMContentLoaded', bindSearchControl);
  setTimeout(bindSearchControl, 0);

  renderHistory();
  prefillSearchInputFromUrl(input());
  syncClear();
}
